import React from 'react';
import { motion } from 'framer-motion';
import { HeartIcon, ShieldCheckIcon, StarIcon } from 'lucide-react';
import { PageHero } from '../components/PageHero';
import { FamilyCaregiverSection } from '../components/FamilyCaregiverSection';
import { TestimonialsSection } from '../components/TestimonialsSection';
import { CTABanner } from '../components/CTABanner';
export function AboutPage() {
  const values = [{
    icon: <HeartIcon className="w-8 h-8 text-primary" data-id="element-249" />,
    title: 'Compassion',
    description: 'We believe care should be rooted in love and understanding, which is why we champion the family caregiver model.'
  }, {
    icon: <ShieldCheckIcon className="w-8 h-8 text-primary" data-id="element-250" />,
    title: 'Trust',
    description: 'We build trust through transparency, rigorous training, and handling all compliance with the utmost professionalism.'
  }, {
    icon: <StarIcon className="w-8 h-8 text-primary" data-id="element-251" />,
    title: 'Excellence',
    description: 'From our on-site training facilities to our dedicated support team, we strive for excellence in every interaction.'
  }];
  return <main data-id="element-252">
      <PageHero title="About Ambient Solutions" subtitle="A Tailored Path to Optimal Health" data-id="element-253" />

      {/* Mission Section */}
      <section className="py-24 px-4 bg-white" data-id="element-254">
        <div className="max-w-7xl mx-auto" data-id="element-255">
          <div className="flex flex-col lg:flex-row items-center gap-16" data-id="element-256">
            <motion.div initial={{
            opacity: 0,
            x: -30
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.6
          }} className="flex-1" data-id="element-257">
              <h2 className="text-sm font-bold text-accent tracking-widest uppercase mb-2" data-id="element-258">
                Who Are We
              </h2>
              <h3 className="text-3xl md:text-4xl font-heading font-bold text-neutral-dark mb-6" data-id="element-259">
                A Tailored Path to Optimal Health
              </h3>
              <p className="text-lg text-neutral-medium mb-6 leading-relaxed" data-id="element-260">
                Ambient Solutions is not a typical home health agency. Instead
                of sending strangers into your home,{' '}
                <strong className="text-primary" data-id="element-261">
                  we help families hire, train, and certify their own family
                  members as professional caregivers.
                </strong>
              </p>
              <p className="text-lg text-neutral-medium mb-6 leading-relaxed" data-id="element-262">
                We also provide skilled nursing and personalized aide services
                for individuals and families who value high-quality, attentive
                care in the comfort of home.
              </p>
              <p className="text-lg text-neutral-medium leading-relaxed" data-id="element-263">
                We specialize in delivering clinically sound, compassionate care
                with dignity, discretion, and excellence — whether through your
                own family caregiver or our professional team.
              </p>
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            x: 30
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.6,
            delay: 0.2
          }} className="flex-1" data-id="element-264">
              <div className="relative" data-id="element-265">
                {/* Main caregiving photo */}
                <div className="rounded-3xl overflow-hidden shadow-xl" data-id="element-266">
                  <img src="https://images.unsplash.com/photo-1576765608535-5f04d1e3f289?auto=format&fit=crop&q=80&w=800" alt="Professional caregiver smiling with elderly patient at home" className="w-full h-auto object-cover" data-id="element-267" />
                </div>

                {/* Brand accent card */}
                <div className="absolute -bottom-6 -left-6 bg-accent-light p-6 rounded-2xl shadow-lg border border-white max-w-[220px] hidden md:block" data-id="element-268">
                  <div className="w-16 h-16 rounded-full border-2 border-accent flex items-center justify-center mx-auto mb-3" data-id="element-269">
                    <span className="font-heading font-bold text-primary text-xs text-center leading-tight" data-id="element-270">
                      AMBIENT
                      <br data-id="element-271" />
                      SOLUTIONS
                    </span>
                  </div>
                  <p className="text-xs text-neutral-medium text-center italic" data-id="element-272">
                    A Tailored Path to Optimal Health.
                  </p>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Our Commitment */}
      <section className="py-24 px-4 bg-primary text-white text-center" data-id="element-273">
        <div className="max-w-4xl mx-auto" data-id="element-274">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} data-id="element-275">
            <h2 className="text-sm font-bold text-accent tracking-widest uppercase mb-4" data-id="element-276">
              Our Commitment
            </h2>
            <p className="text-2xl md:text-3xl font-heading leading-relaxed" data-id="element-277">
              "We believe the best caregiver is often already in your family.
              Our job is to make that possible — with professional training,
              clinical oversight, and peace of mind. Because exceptional care
              should feel seamless."
            </p>
          </motion.div>
        </div>
      </section>

      <FamilyCaregiverSection data-id="element-278" />

      {/* Our Values */}
      <section className="py-24 px-4 bg-warm" data-id="element-279">
        <div className="max-w-7xl mx-auto" data-id="element-280">
          <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-281">
            <motion.h2 initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} className="text-3xl md:text-4xl font-heading font-bold text-neutral-dark mb-4" data-id="element-282">
              Our Core Values
            </motion.h2>
            <motion.p initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: 0.1
          }} className="text-lg text-neutral-medium" data-id="element-283">
              The principles that guide everything we do at Ambient Solutions.
            </motion.p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-284">
            {values.map((value, index) => <motion.div key={index} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: index * 0.15
          }} className="bg-white p-8 rounded-2xl shadow-sm border border-gray-100 text-center" data-id="element-285">
                <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-6" data-id="element-286">
                  {value.icon}
                </div>
                <h3 className="text-xl font-heading font-bold text-neutral-dark mb-4" data-id="element-287">
                  {value.title}
                </h3>
                <p className="text-neutral-medium leading-relaxed" data-id="element-288">
                  {value.description}
                </p>
              </motion.div>)}
          </div>
        </div>
      </section>

      <TestimonialsSection data-id="element-289" />
      <CTABanner data-id="element-290" />
    </main>;
}